import * as Sentry from "@sentry/react";
import axios from "axios";
import toast from "react-hot-toast";
import { resetAnalytics } from "./analytics";
import { clearSession } from "./auth-session";
// import { getToken } from "helpers/utils/auth-provider";

const apiURLEndpoint = (
  process.env.REACT_APP_API_URL || "https://api.autumnplatform.com"
).replace(/\/$/, "");

const onResponse = (response: any) => response;
const onResponseError = (error: any) => {
  const status = error.response?.status;
  const url = error.config?.url || "unknown";
  const method = error.config?.method?.toUpperCase() || "unknown";

  // Capture critical API errors only (stay within Sentry free tier)
  if (status && status >= 500) {
    // 500+ errors are server bugs - always capture
    Sentry.captureException(error, {
      tags: {
        type: "api_error",
        status,
        endpoint: url,
        method,
      },
      level: "error",
      extra: {
        responseData: error.response?.data,
        requestData: error.config?.data,
      },
    });
  } else if (status === 403) {
    // Permission errors - might indicate authorization bugs
    Sentry.captureException(error, {
      tags: {
        type: "permission_error",
        status,
        endpoint: url,
        method,
      },
      level: "warning",
    });
  }
  // Note: 401 errors are expected (session expiry) - don't send to Sentry
  // Note: 400 errors are usually validation - sampled at 20% in sentry.ts

  // Property-switch hook (helpers/hooks/use-property-switch.ts) sets this
  // window flag right before initiating the switch + token rotation, and
  // never clears it (the subsequent hard reload destroys the page). It tells
  // the interceptor: "I'm doing a controlled identity swap, ignore stray
  // 401s from in-flight requests that captured stale closures." Without
  // this, mounted dashboard hooks whose queryFn captured the old user.id
  // can race the swap and trigger a spurious 'Session expired' toast even
  // though the user is correctly authenticated as the new account.
  const suppressAuthRedirect =
    typeof window !== "undefined" &&
    (window as unknown as { __SUPPRESS_AUTH_REDIRECT__?: boolean })
      .__SUPPRESS_AUTH_REDIRECT__ === true;

  // A 401 with no token in localStorage isn't an expiry — the user has logged
  // out (all logout handlers remove the token, then navigate). During the brief
  // window before the browser unloads, in-flight/refetch requests fire with an
  // empty Bearer and 401. Without this guard each one shows a 'Session expired'
  // toast and schedules its own reload, fighting the logout navigation.
  const hasToken =
    typeof window !== "undefined" && !!localStorage.getItem("token");

  if (
    status === 401 &&
    hasToken &&
    !error.config.url.includes("login") &&
    !suppressAuthRedirect
  ) {
    // handle unauthorized
    resetAnalytics();
    toast.error("Session expired. Please login again.");

    setTimeout(() => {
      clearSession();
      window.location.replace("/");
    }, 1000);
  }
  return Promise.reject(error);
};

function onRequest(config: any) {
  //   const token = getToken();
  // eslint-disable-next-line no-param-reassign
  config.headers["Authorization"] =
    "Bearer " + (localStorage.getItem("token") || "");
  return config;
}

// create client instance
const client = axios.create({
  baseURL: apiURLEndpoint,
});
client.interceptors.request.use(onRequest);
client.interceptors.response.use(onResponse, onResponseError);

// const AUTH_TOKEN = getToken();
// client.defaults.headers.common.Authorization = AUTH_TOKEN;

export default client;
